// src/components/shared/nav/Sidebar.tsx
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiBell, FiHelpCircle, FiKey, FiMenu, FiUser, FiX } from "react-icons/fi";

import { MENU, colorKeyFor } from "./menu.config";
import { LOGO } from "@/components/auth/assets";

import type {
  RuntimeMenuItem,
  RuntimeMenuGroup,
  RuntimeMenuLink,
  MenuItem,
  MenuGroup,
  MenuLink,
} from "./types";
import { useActiveRoute } from "./useActiveRoute";
import { SidebarGroup } from "./SidebarGroup";
import { SidebarLink } from "./SidebarLink";

import { getAuth, logout } from "@/api/client";

type AuthUser = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string;
  email: string | null;
  roleId: number;
  roleName: string | null;
  modules?: string[];
};

function initials(name?: string) {
  if (!name) return "U";
  const bits = name.trim().split(/\s+/).slice(0, 2);
  return bits.map((b) => b[0]?.toUpperCase() ?? "").join("") || "U";
}

function normalize(path: string): string {
  if (!path) return "/";
  const p = String(path).trim();
  if (p.length > 1 && p.endsWith("/")) return p.slice(0, -1);
  return p;
}

function normSlug(v: unknown): string {
  return String(v ?? "").trim().toLowerCase();
}

const ROUTE_MODULE_RULES: Record<string, string[]> = {
  "/dashboard": ["dashboard"],

  "/roles": ["roles"],
  "/modules": ["modules"],
  "/email-templates": ["email-templates"],

  "/hr/users": ["users"],
  "/hr/permissions": ["users-permissions"],

  "/products": ["products"],
  "/promos": ["promos"],
  "/payment-links": ["payment-links"],
  "/suppliers": ["suppliers"],
  "/affiliates": ["affiliates"],
  "/customers": ["customers"],
  "/leads": ["leads"],

  "/tour/tour-types": ["tour-types"],
  "/tour/tour-packages": ["tour-packages"],

  "/bookings": ["bookings"],
  "/flight-bookings": ["bookings"],
  "/hotel-bookings": ["bookings"],
  // Matches the backend's requirePermission("hotels") on /hotels/catalogue/*.
  "/hotel-catalogue": ["hotels"],
  "/flights/activity-logs": ["bookings"],

  // One entry, three tabs. The backend guards every locations write with the
  // "locations" module; the per-entity slugs stay valid so roles granted only
  // "cities" (or "countries" / "regions") keep the access they already had.
  // The page itself hides the tabs a user has no grant for.
  "/locations": ["locations", "countries", "regions", "cities"],
  "/currencies": ["currencies"],
  "/flights/airlines": ["airlines"],
  "/flights/airports": ["airports"],
};

function getRequiredModulesForPath(path: string): string[] | undefined {
  const p = normalize(path);

  if (Object.prototype.hasOwnProperty.call(ROUTE_MODULE_RULES, p)) {
    return ROUTE_MODULE_RULES[p];
  }

  let bestKey: string | null = null;
  for (const key of Object.keys(ROUTE_MODULE_RULES)) {
    const k = normalize(key);
    if (k === "/") continue;

    const isPrefix = p === k || (p.startsWith(k + "/") && k.length < p.length);
    if (!isPrefix) continue;

    if (!bestKey || k.length > normalize(bestKey).length) {
      bestKey = k;
    }
  }

  return bestKey ? ROUTE_MODULE_RULES[bestKey] : undefined;
}

function userHasAccessToRoute(to: string, userModules: string[] | undefined | null): boolean {
  const requiredModules = getRequiredModulesForPath(to);

  // Deny-by-default: a menu link with NO declared module rule is hidden. Every
  // module link must map to its RBAC slug in ROUTE_MODULE_RULES above — this way
  // a link can never leak into the sidebar for a user who lacks its permission,
  // and new links are hidden until their permission is explicitly declared.
  if (!requiredModules || requiredModules.length === 0) return false;
  if (!userModules || userModules.length === 0) return false;

  const userSet = new Set(userModules.map(normSlug));
  return requiredModules.some((slug) => userSet.has(normSlug(slug)));
}

function filterMenuByModules(
  items: MenuItem[],
  userModules: string[] | undefined | null
): MenuItem[] {
  const result: MenuItem[] = [];

  for (const item of items) {
    if (item.type === "link") {
      const link = item as MenuLink;
      if (userHasAccessToRoute(link.to, userModules)) result.push(link);
      continue;
    }

    if (item.type === "group") {
      const group = item as MenuGroup;

      if ((group as any).disabled) {
        result.push(group);
        continue;
      }

      const children = filterMenuByModules(group.children as MenuItem[], userModules);

      if (children.length > 0) {
        result.push({ ...group, children });
      }
      continue;
    }

    result.push(item);
  }

  return result;
}

function cleanupMenuStructure(items: MenuItem[]): MenuItem[] {
  const cleaned: MenuItem[] = [];

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];

    if (item.type === "label") {
      // A heading only earns its place if ITS OWN stretch has something left
      // after permission filtering. A section heading runs to the next divider;
      // a sub-heading stops at the next heading. Without this, hidden links
      // leave a run of empty headings stacked on top of each other.
      const rest = items.slice(i + 1);
      const endsSection = (next: MenuItem) =>
        next.type === "divider" ||
        (item.variant !== "section" && next.type === "label");

      const endIndex = rest.findIndex(endsSection);
      const scope = endIndex === -1 ? rest : rest.slice(0, endIndex);

      const hasVisibleContent = scope.some(
        (next) => next.type === "link" || next.type === "group"
      );
      if (!hasVisibleContent) continue;

      const prev = cleaned[cleaned.length - 1];
      if (prev?.type === "divider") cleaned.pop();

      cleaned.push(item);
      continue;
    }

    if (item.type === "divider") {
      const prev = cleaned[cleaned.length - 1];
      const hasVisibleContentAhead = items
        .slice(i + 1)
        .some((next) => next.type === "link" || next.type === "group");

      if (!prev) continue;
      if (prev.type === "divider" || prev.type === "label") continue;
      if (!hasVisibleContentAhead) continue;

      cleaned.push(item);
      continue;
    }

    cleaned.push(item);
  }

  while (cleaned.length && cleaned[cleaned.length - 1]?.type === "divider") {
    cleaned.pop();
  }

  return cleaned;
}

function addRuntimeFlags(
  items: MenuItem[],
  isActiveLink: (to: string) => boolean,
  isActiveGroup: (children: MenuItem[]) => boolean
): RuntimeMenuItem[] {
  return items.map((item) => {
    if (item.type === "group") {
      const group = item as RuntimeMenuGroup;
      const children = addRuntimeFlags(group.children as MenuItem[], isActiveLink, isActiveGroup);
      const active = isActiveGroup(children as any);
      return { ...group, children: children as any, __active: active, __defaultOpen: active };
    }

    if (item.type === "link") {
      const link = item as RuntimeMenuLink;
      const active = !link.disabled && isActiveLink(link.to);
      return { ...link, __active: active };
    }

    return item as RuntimeMenuItem;
  });
}

function renderMenuItems(
  items: RuntimeMenuItem[],
  indentLevel: number,
  parentColorKey: ReturnType<typeof colorKeyFor> | null,
  isActiveLink: (to: string) => boolean
) {
  return items.map((item, idx) => {
    if (item.type === "link") {
      const link = item as RuntimeMenuLink;
      const ck = parentColorKey ?? colorKeyFor(link.label);

      return (
        <SidebarLink
          key={`${indentLevel}-link-${idx}-${link.to}`}
          to={link.to}
          icon={link.icon}
          label={link.label}
          active={!!link.__active}
          disabled={link.disabled}
          colorKey={ck}
          indentLevel={indentLevel}
        />
      );
    }

    if (item.type === "group") {
      const group = item as RuntimeMenuGroup;
      const groupCk = colorKeyFor(group.label);

      return (
        <SidebarGroup
          key={`${indentLevel}-group-${idx}-${group.label}`}
          label={group.label}
          icon={group.icon}
          defaultOpen={group.__defaultOpen}
          active={group.__active}
          colorKey={groupCk}
          disabled={(group as any).disabled}
        >
          {renderMenuItems(group.children as RuntimeMenuItem[], indentLevel + 1, groupCk, isActiveLink)}
        </SidebarGroup>
      );
    }

    if (item.type === "divider") {
      return <hr key={`${indentLevel}-div-${idx}`} className="my-2 border-t border-gray-200" />;
    }

    if (item.type === "label") {
      // Sub-headings sit quieter than section headings and carry a hairline to
      // the right, so a long section reads as distinct blocks at a glance.
      if (item.variant === "sub") {
        return (
          <div
            key={`${indentLevel}-lbl-${idx}`}
            className="flex items-center gap-2 px-3 pb-1 pt-3"
          >
            {item.icon && <span className="text-gray-400">{item.icon}</span>}
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              {item.label}
            </span>
            <span className="h-px flex-1 bg-gray-100" />
          </div>
        );
      }

      return (
        <div
          key={`${indentLevel}-lbl-${idx}`}
          className="flex items-center gap-2 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-500"
        >
          {item.icon && <span className="text-gray-400">{item.icon}</span>}
          <span>{item.label}</span>
        </div>
      );
    }

    return null;
  });
}

export default function SidebarShell({ children }: { children: ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);

  const [user, setUser] = useState<AuthUser | null>(() => {
    const a = getAuth();
    return (a.user as AuthUser) ?? null;
  });

  useEffect(() => {
    function handleUpdated(e: Event) {
      const detail = (e as CustomEvent).detail as ReturnType<typeof getAuth>;
      setUser((detail?.user as AuthUser) ?? null);
    }
    function handleCleared() {
      setUser(null);
    }
    window.addEventListener("auth:updated", handleUpdated as EventListener);
    window.addEventListener("auth:cleared", handleCleared);
    return () => {
      window.removeEventListener("auth:updated", handleUpdated as EventListener);
      window.removeEventListener("auth:cleared", handleCleared);
    };
  }, []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setUserMenu(false);
        setSidebarOpen(false);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const { isActiveLink, isActiveGroup } = useActiveRoute();

  const menuWithActive: RuntimeMenuItem[] = useMemo(() => {
    const userModules = user?.modules ?? null;
    const filteredMenu: MenuItem[] = cleanupMenuStructure(
      filterMenuByModules(MENU, userModules)
    );
    return addRuntimeFlags(filteredMenu, isActiveLink, isActiveGroup);
  }, [isActiveGroup, isActiveLink, user?.modules]);

  const displayName =
    (user?.firstName && user?.lastName && `${user.firstName} ${user.lastName}`) ||
    user?.firstName ||
    user?.username ||
    (typeof user?.email === "string" ? user.email.split("@")[0] : "User");

  const roleName =
    (user?.roleName && String(user.roleName).trim()) ||
    (user?.roleId ? `Role #${user.roleId}` : "User");

  return (
    <div className="flex min-h-screen bg-gray-50">
      <aside
        role="navigation"
        aria-label="Primary"
        // Full-height flex column so the nav below the profile header owns the
        // remaining space and scrolls inside it — the page itself never grows
        // to fit the menu. Sticky on desktop keeps it pinned while content
        // scrolls.
        className={`fixed inset-y-0 left-0 z-40 flex w-72 transform flex-col border-r border-gray-200 bg-white
                    transition-transform duration-200 md:sticky md:top-0 md:h-screen md:translate-x-0
                    ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-gray-200 px-4 py-4">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-gray-100 text-gray-600">
            <span className="text-xs font-semibold">{initials(displayName)}</span>
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold text-gray-900">{displayName}</div>
            <div className="text-xs text-gray-500">{roleName}</div>
          </div>
        </div>

        {/* min-h-0 lets this flex child shrink below its content height, which
            is what makes overflow-y-auto actually scroll. */}
        <nav className="slim-scroll min-h-0 flex-1 space-y-1 overflow-y-auto px-2 py-3">
          {renderMenuItems(menuWithActive, 0, null, isActiveLink)}
        </nav>
      </aside>

      {sidebarOpen && (
        <button
          aria-label="Close menu overlay"
          className="fixed inset-0 z-30 bg-black/30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex min-h-screen min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-3">
          <button
            className="grid h-9 w-9 place-items-center rounded-full text-gray-600 hover:bg-gray-100 md:hidden"
            onClick={() => setSidebarOpen((s) => !s)}
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? <FiX /> : <FiMenu />}
          </button>

          <img src={LOGO} alt="Tranzy" className="h-8" />

          <div className="relative ml-2 max-w-xl flex-1">
            <input
              placeholder="Search…"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
            />
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button className="grid h-9 w-9 place-items-center rounded-full text-gray-600 hover:bg-gray-100">
              <FiBell />
            </button>
            <button className="grid h-9 w-9 place-items-center rounded-full text-gray-600 hover:bg-gray-100">
              <FiHelpCircle />
            </button>

            <div className="relative">
              <button
                onClick={() => setUserMenu((v) => !v)}
                className="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-gray-100"
              >
                <div className="grid h-8 w-8 place-items-center rounded-full bg-gray-100 text-gray-700">
                  <span className="text-xs font-semibold">{initials(displayName)}</span>
                </div>
                <span className="hidden text-sm text-gray-800 sm:block">{displayName}</span>
              </button>

              {userMenu && (
                <div className="absolute right-0 mt-2 w-48 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
                  <div className="px-3 py-2 text-xs text-gray-500">
                    Signed in as{" "}
                    <span className="font-medium text-gray-700">{roleName}</span>
                  </div>
                  <Link
                    to="/account"
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setUserMenu(false)}
                  >
                    <FiUser /> My Account
                  </Link>
                  <Link
                    to="/account/change-password"
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setUserMenu(false)}
                  >
                    <FiKey /> Change Password
                  </Link>
                  <button
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    onClick={async () => {
                      setUserMenu(false);
                      await logout();
                    }}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6">{children}</main>
      </div>
    </div>
  );
}
